// team-grid-infinite
import { onDestroy, onView } from "@/modules/_";
import { Raf, Resize } from "@lib/subs";
import gsap, { reduced } from "@lib/gsap";
import { colors } from "@/utils/colors";

// page bg is blueberry-400, so blueberry family excluded — dark blueberry reads as "blue chip on blue page"
const BG_PALETTE_FAMILIES = [
  "ube",
  "matcha",
  "lemon",
  "tangerine",
  "dragonfruit",
  "pomegranate",
  "lime",
  "slushie",
] as const;
const BG_PALETTE_SHADES = [400, 500, 600, 700, 800, 900, 950] as const;

// ~3:1 contrast against white text; lighter pastels (lemon-400, slushie-400) fall above this and are rejected
const MAX_BG_LUMINANCE = 0.3;

function relLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

// Diacritic-folded, lowercased, whitespace-collapsed — so "José" matches "jose".
function foldText(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

const BG_PALETTE: string[] = (() => {
  const out: string[] = [];
  for (const family of BG_PALETTE_FAMILIES) {
    const fam = colors[family] as Record<number, string | undefined>;
    for (const shade of BG_PALETTE_SHADES) {
      const hex = fam[shade];
      if (hex && relLuminance(hex) <= MAX_BG_LUMINANCE) out.push(hex);
    }
  }
  return out;
})();

const COLS = 6;
const GAP = 40;

// The grid tiles a virtual torus instead of the naive COLS-wide wrap (which repeats the same
// 6 people every screen). The torus is roughly square — each member placed ~TORUS_APPEARANCES
// times, so a big roster maximizes distinct faces per screen in both drag axes — with a
// TORUS_MIN_SIZE floor so small rosters still travel several screens before the pattern wraps.
const TORUS_APPEARANCES = 4;
const TORUS_MIN_SIZE = 24;

// A member's copies are kept at least this many cells apart (Chebyshev, torus-wrapped) so two of
// the same face don't sit near each other in one viewport. Repeats can't be eliminated — a
// V-cell viewport over an N-person roster averages V/N copies of *someone* regardless of layout —
// but spacing stops the pairs from reading as a bug. Repair is best-effort over a few bounded
// passes; a dense torus (small roster) may be impossible to fully satisfy.
const NEIGHBOR_EXCLUSION_RADIUS = 2;
const LAYOUT_REPAIR_PASSES = 3;

const INERTIA_RESISTANCE = 1500;
const VELOCITY_EMA_MS = 80;
const DRAG_THRESHOLD = 4;

const CENTER_SCALE = 1;
const EDGE_SCALE = 0;
const ENTER_MARGIN = 0.1;
const EXIT_MARGIN = 0.4;
const PLAY_DURATION = 0.6;
const PLAY_EASE = "power3.out";
const PLAY_STAGGER = 0.06;
// Cap the stagger tail — a zoom-out (or the first scroll-into-view during the roster load) can
// reveal dozens of cards at once, and a long tail leaves visible cells reading as empty gaps
// mid-reveal. Kept short so the wave closes fast.
const PLAY_DELAY_MAX = 0.4;
// Mass-reveal cap: only the N entering cards nearest the viewport center get the play-in
// tween; the rest snap into place. A full zoom-out fires 80+ concurrent tweens otherwise
// (measured ~49fps with 5fps spikes), and at low zoom the far cards are too small to miss it.
const PLAY_MAX_ANIMATED = 40;
const IMG_FROM_SCALE = 2;
const IMG_TO_SCALE = 1;
const MOUNT_SCALE = EDGE_SCALE;

const WHEEL_ENABLED_DEFAULT = false;
const WHEEL_MULTIPLIER = 1;
const WHEEL_LINE_HEIGHT_PX = 16;
const WHEEL_PAGE_HEIGHT_PX = 800;

// Fully zoomed-in is the resting state; zoom-out reveals more of the crowd.
const ZOOM_MAX = 1;
// Floor on zoom-out. Lower values expose more cells at once, and in-view duplicates scale with
// visible-cell-count / roster-size — so a deep zoom-out makes repeated faces near-certain. 0.55
// keeps the crowd readable without opening a window wide enough to guarantee dupes.
const ZOOM_MIN = 0.55;
const ZOOM_STEP = 0.75;
const ZOOM_DURATION = 0.8;
const ZOOM_EASE = "power3.inOut";

// On open, frame ~OPEN_COLS card columns across the viewport width. On a phone one card nearly
// fills the width at full zoom, so the grid would open on a single face; fitting a couple of
// columns starts it zoomed-out enough to read as a crowd. Wider screens already fit more than this
// at full zoom, so the computed value clamps back to ZOOM_MAX — desktop/tablet are unaffected.
const OPEN_COLS = 3;

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_FLY_MIN_DURATION = 0.6;
const SEARCH_FLY_MAX_DURATION = 1.4;
const SEARCH_FLY_SPEED_PX_S = 3000;
const SEARCH_FLY_EASE = "power3.inOut";
const SEARCH_DIM_FILTER = "grayscale(1) brightness(0.6)";
const SEARCH_DIM_TRANSITION = "filter 0.35s ease";

const BUFFER_CELLS = 2;

const RESIZE_REBUILD_THRESHOLD_PX = 2;

// Webflow renders at most 100 Collection List items into the DOM per page; we follow the
// native "next" link chain to splice the rest in. Safety cap on how many pages we'll follow.
const PAGINATION_MAX_PAGES = 50;

// Hard cap on how long the one-time roster extend may defer waiting for an idle moment, so a
// user who drags continuously still eventually gets the full roster folded in.
const EXTEND_DEFER_MAX_MS = 3000;

const IDLE_PREFETCH_FALLBACK_BATCH = 4;
const IDLE_PREFETCH_FALLBACK_DELAY_MS = 200;
const IDLE_PREFETCH_TIMEOUT_MS = 4000;
const PREFETCH_AFTER_LOAD_DELAY_MS = 500;

const DEBUG = false;
const DEBUG_INTERVAL_MS = 2000;
const DEBUG_LONG_FRAME_MS = 50;

type SlotEntry = {
  el: HTMLElement;
  cardEl: HTMLElement | null;
  col: number;
  row: number;
  itemIndex: number;
  active: boolean;
};

// Torus dimensions for a roster of n: a roughly-square virtual grid sized so each member
// appears ~TORUS_APPEARANCES times, floored at TORUS_MIN_SIZE. Shared by the initial build and
// the background extend so both compute identical geometry.
function computeTorus(n: number): { vCols: number; vRows: number } {
  const vCols = Math.max(
    TORUS_MIN_SIZE,
    Math.ceil(Math.sqrt(n * TORUS_APPEARANCES)),
  );
  const vRows = Math.max(
    TORUS_MIN_SIZE,
    Math.ceil((n * TORUS_APPEARANCES) / vCols),
  );
  return { vCols, vRows };
}

// Follow Webflow's native pagination, parsing each "next" page and splicing its CMS items into
// the live source list. Runs in the BACKGROUND after first paint; extendRoster folds the
// appended tail into the torus + search index once it resolves.
async function loadPaginatedItems(
  cmsWrapper: HTMLElement,
  cmsList: HTMLElement,
  signal: AbortSignal,
): Promise<void> {
  const dynList = cmsList.closest<HTMLElement>(".w-dyn-list") ?? cmsWrapper;

  const firstHref = dynList
    .querySelector<HTMLAnchorElement>(".w-pagination-next")
    ?.getAttribute("href");
  if (!firstHref) return;

  // Seed with the current URL so a self-referential "next" can't loop us back onto page 1.
  const seen = new Set<string>([location.href]);
  let href: string | null = new URL(firstHref, location.href).href;

  for (
    let page = 0;
    href && page < PAGINATION_MAX_PAGES && !seen.has(href);
    page++
  ) {
    seen.add(href);
    let html: string;
    try {
      const res = await fetch(href, { signal });
      if (!res.ok) break;
      html = await res.text();
    } catch {
      break; // aborted (navigated away) or offline — proceed with what we already have
    }

    const doc = new DOMParser().parseFromString(html, "text/html");
    const list = doc.querySelector('[team-grid-infinite="cms-list"]');
    if (!list) break;

    const fresh = list.querySelectorAll<HTMLElement>(
      '[team-grid-infinite="cms-item"]',
    );
    if (fresh.length === 0) break;
    const frag = document.createDocumentFragment();
    fresh.forEach((item) => frag.appendChild(document.importNode(item, true)));
    cmsList.appendChild(frag);

    const nextHref = (list.closest(".w-dyn-list") ?? doc)
      .querySelector<HTMLAnchorElement>(".w-pagination-next")
      ?.getAttribute("href");
    href = nextHref ? new URL(nextHref, location.href).href : null;
  }
}

export default async function (element: HTMLElement, dataset: DOMStringMap) {
  const wheelOff = dataset.wheel === "off" || dataset.wheel === "false";
  const wheelEnabled = WHEEL_ENABLED_DEFAULT && !wheelOff;
  const prefetchEnabled =
    dataset.prefetch !== "off" && dataset.prefetch !== "false";

  const cmsWrapper = element.querySelector<HTMLElement>(
    '[team-grid-infinite="cms-wrapper"]',
  );
  const cmsList = element.querySelector<HTMLElement>(
    '[team-grid-infinite="cms-list"]',
  );
  if (!cmsWrapper || !cmsList) return;

  // Hide the source list immediately — Webflow's paginated list must never flash. opacity (not
  // visibility) keeps the real roster in the a11y tree; the canvas clones are aria-hidden.
  const prevWrapperOpacity = cmsWrapper.style.opacity;
  const prevWrapperPointer = cmsWrapper.style.pointerEvents;
  cmsWrapper.style.opacity = "0";
  cmsWrapper.style.pointerEvents = "none";

  // The pagination wrapper is a sibling of the items list inside .w-dyn-list (not necessarily a
  // descendant of cms-wrapper), so hide it directly — unconditionally, on frame 1, before the
  // canvas exists. Tying this to "has a next page" would leak the Prev/Next chrome on single-
  // or last-page rosters that still render the controls.
  const dynList = cmsList.closest<HTMLElement>(".w-dyn-list") ?? cmsWrapper;
  const paginationWraps = Array.from(
    dynList.querySelectorAll<HTMLElement>(".w-pagination-wrapper"),
  );
  const prevPaginationDisplay = paginationWraps.map((el) => el.style.display);
  paginationWraps.forEach((el) => (el.style.display = "none"));
  const hasNextPage = !!dynList
    .querySelector<HTMLAnchorElement>(".w-pagination-next")
    ?.getAttribute("href");

  // Abort scaffolding for the background page load. Registered before the build so a navigation
  // mid-load aborts the fetch and the deferred extend bails out.
  let destroyed = false;
  const loadAbort = new AbortController();
  onDestroy(() => {
    destroyed = true;
    loadAbort.abort();
  });

  // Build IMMEDIATELY from the page-1 items already in the DOM (Webflow renders up to 100) so the
  // section paints in <100ms instead of waiting on the whole paginated roster. The rest of the
  // roster loads in the background and folds in once via extendRoster (defined below). These
  // roster-derived bindings are `let` because extendRoster reassigns them; every consumer reads
  // them lexically through closure, so the reassignment takes effect with no rewrites.
  let items = Array.from(
    cmsList.querySelectorAll<HTMLElement>('[team-grid-infinite="cms-item"]'),
  );
  if (items.length === 0) return;
  let N = items.length;
  let rosterComplete = !hasNextPage;
  let { vCols, vRows } = computeTorus(N);

  let itemW = items[0].getBoundingClientRect().width;
  let itemH = items[0].getBoundingClientRect().height;
  let cellW = itemW + GAP;
  let cellH = itemH + GAP;

  const prevSectionPosition = element.style.position;
  const prevSectionTouchAction = element.style.touchAction;
  const prevSectionUserSelect = element.style.userSelect;
  element.style.position = element.style.position || "relative";
  element.style.touchAction = "none";
  element.style.userSelect = "none";

  const viewport = document.createElement("div");
  viewport.setAttribute("aria-hidden", "true");
  Object.assign(viewport.style, {
    position: "absolute",
    inset: "0",
    overflow: "hidden",
    touchAction: "none",
    cursor: "grab",
    userSelect: "none",
  } as Partial<CSSStyleDeclaration>);

  const inner = document.createElement("div");
  Object.assign(inner.style, {
    position: "absolute",
    inset: "0",
    willChange: "transform",
    // Zoom math assumes scale around the translated origin, not the box center.
    transformOrigin: "0 0",
  } as Partial<CSSStyleDeclaration>);
  viewport.appendChild(inner);
  element.appendChild(viewport);

  const itemCards: (HTMLElement | null)[] = new Array(N).fill(null);
  // Detached duplicate clones per item, reused when the same member is visible in two cells —
  // without this every duplicate recycle builds a fresh clone, churning GC over long sessions.
  const spareCards = new Map<number, HTMLElement[]>();
  // Imgs per card, resolved once at clone time — playCard/recycle run hot during drags and zooms.
  const cardImgs = new WeakMap<HTMLElement, HTMLImageElement[]>();
  const activeByKey = new Map<string, SlotEntry>();
  const pool: SlotEntry[] = [];

  function getCardImgs(card: HTMLElement): HTMLImageElement[] {
    let imgs = cardImgs.get(card);
    if (!imgs) {
      imgs = Array.from(card.querySelectorAll("img"));
      cardImgs.set(card, imgs);
    }
    return imgs;
  }

  function cellKey(c: number, r: number) {
    return c + "," + r;
  }

  function buildLayout(): number[] {
    const total = vCols * vRows;
    const seq: number[] = [];
    const deck = Array.from({ length: N }, (_, i) => i);
    while (seq.length < total) {
      for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
      }
      // Deck seam: don't let a deck open with the item the previous one closed on.
      if (N > 1 && seq.length > 0 && deck[0] === seq[seq.length - 1]) {
        const mid = deck.length >> 1;
        [deck[0], deck[mid]] = [deck[mid], deck[0]];
      }
      for (const v of deck) seq.push(v);
    }
    seq.length = total;

    // A copy is "too close" if another copy of the same member sits within NEIGHBOR_EXCLUSION_RADIUS
    // cells (Chebyshev distance, torus-wrapped) — a far wider guard than the old 4-neighbor check,
    // so two of the same face don't land near each other inside one viewport.
    const R = NEIGHBOR_EXCLUSION_RADIUS;
    const tooClose = (i: number, v: number) => {
      const ci = i % vCols;
      const ri = (i / vCols) | 0;
      for (let dr = -R; dr <= R; dr++) {
        const rr = (((ri + dr) % vRows) + vRows) % vRows;
        for (let dc = -R; dc <= R; dc++) {
          if (dr === 0 && dc === 0) continue;
          const cc = (((ci + dc) % vCols) + vCols) % vCols;
          if (seq[rr * vCols + cc] === v) return true;
        }
      }
      return false;
    };
    // Repair: swap each offender toward a slot that satisfies the radius at both ends. Bounded
    // passes because a dense torus can't always be fully satisfied; each pass only improves
    // spacing, never regresses it, and stops early once a pass finds nothing to fix.
    if (N > 2) {
      for (let pass = 0; pass < LAYOUT_REPAIR_PASSES; pass++) {
        let swaps = 0;
        for (let i = 0; i < total; i++) {
          if (!tooClose(i, seq[i])) continue;
          for (let j = i + 1; j < total; j++) {
            if (
              seq[j] !== seq[i] &&
              !tooClose(j, seq[i]) &&
              !tooClose(i, seq[j])
            ) {
              [seq[i], seq[j]] = [seq[j], seq[i]];
              swaps++;
              break;
            }
          }
        }
        if (swaps === 0) break;
      }
    }
    return seq;
  }

  let layout = buildLayout();

  function itemAt(col: number, row: number) {
    const c = ((col % vCols) + vCols) % vCols;
    const r = ((row % vRows) + vRows) % vRows;
    return layout[r * vCols + c];
  }

  function createCard(srcItem: HTMLElement): HTMLElement {
    const card = srcItem.cloneNode(true) as HTMLElement;
    card.setAttribute("aria-hidden", "true");
    card.removeAttribute("team-grid-infinite");
    Object.assign(card.style, {
      width: "100%",
      height: "100%",
      transformOrigin: "50% 50%",
      transform: `scale(${MOUNT_SCALE})`,
      opacity: "0",
      // No will-change here: playCard promotes layers only while tweening — hundreds of cards
      // with permanent will-change blow GPU layer budgets at low zoom.
      // GSAP owns transform/opacity; the search dim (filter) is the only CSS-transitioned property.
      transition: SEARCH_DIM_TRANSITION,
    } as Partial<CSSStyleDeclaration>);
    const sizesHint = Math.round(itemW) + "px";
    const imgs: HTMLImageElement[] = [];
    card.querySelectorAll<HTMLElement>("*").forEach((el) => {
      el.style.pointerEvents = "none";
      if (el instanceof HTMLImageElement) {
        el.draggable = false;
        // Override Webflow's default sizes (often 100vw) to avoid fetching a larger srcset entry than needed.
        el.sizes = sizesHint;
        el.loading = "eager";
        el.decoding = "async";
        // Parallax-zoom: image starts zoomed in, settles to 1 as the card plays in. Wrapper clips.
        el.style.transformOrigin = "50% 50%";
        el.style.transform = `scale(${IMG_FROM_SCALE})`;
        imgs.push(el);
      }
    });
    cardImgs.set(card, imgs);

    if (BG_PALETTE.length > 0) {
      const bg = BG_PALETTE[Math.floor(Math.random() * BG_PALETTE.length)];
      card
        .querySelectorAll<HTMLElement>(".about_team_grid_pos_w")
        .forEach((el) => {
          el.style.backgroundColor = bg;
        });
    }

    return card;
  }

  function ensureItemCard(idx: number): HTMLElement {
    prefetched[idx] = 1;
    const cached = itemCards[idx];
    if (cached && !cached.parentNode) return cached;
    const spare = spareCards.get(idx)?.pop();
    if (spare) return spare;
    if (!cached) {
      const card = createCard(items[idx]);
      itemCards[idx] = card;
      return card;
    }
    return createCard(items[idx]);
  }

  let prefetched = new Uint8Array(N);
  let prefetchCursor = 0;
  let cancelPrefetch: (() => void) | null = null;

  function prefetchItemImages(idx: number) {
    if (prefetched[idx]) return;
    prefetched[idx] = 1;
    const sizesHint = Math.round(itemW) + "px";
    items[idx].querySelectorAll<HTMLImageElement>("img").forEach((srcImg) => {
      const pre = new Image();
      pre.setAttribute("fetchpriority", "low");
      pre.decoding = "async";
      pre.sizes = sizesHint;
      if (srcImg.srcset) pre.srcset = srcImg.srcset;
      if (srcImg.src) pre.src = srcImg.src;
    });
  }

  function isWarm(idx: number) {
    return prefetched[idx] === 1 && itemCards[idx] !== null;
  }

  // Warm an item fully: preload its images, pre-build its card clone, and pre-decode the
  // clone's images — so a card's first paint is pure appendChild + composite. AVIF decode is
  // a few ms per image; without this, revealing 60+ fresh cards at once stacks those
  // milliseconds into long frames on the render thread.
  function warmItem(idx: number) {
    prefetchItemImages(idx);
    if (!itemCards[idx]) itemCards[idx] = createCard(items[idx]);
    for (const img of getCardImgs(itemCards[idx]!)) {
      // Rejects for broken/still-loading-then-aborted images; first paint just decodes then.
      img.decode().catch(() => {});
    }
  }

  // Wait for `load` + buffer before kicking off prefetches — keeps us off the LCP critical path even beyond fetchpriority="low".
  function startPrefetchWhenReady() {
    if (!prefetchEnabled) return;
    const begin = () => {
      const t = window.setTimeout(() => {
        cancelPrefetch = null;
        scheduleIdlePrefetch();
      }, PREFETCH_AFTER_LOAD_DELAY_MS);
      cancelPrefetch = () => window.clearTimeout(t);
    };
    if (document.readyState === "complete") {
      begin();
    } else {
      window.addEventListener("load", begin, { once: true });
      cancelPrefetch = () => window.removeEventListener("load", begin);
    }
  }

  function scheduleIdlePrefetch() {
    while (prefetchCursor < N && isWarm(prefetchCursor)) prefetchCursor++;
    if (prefetchCursor >= N) return;
    const ric = window.requestIdleCallback?.bind(window);
    if (ric) {
      const handle = ric(
        (deadline) => {
          cancelPrefetch = null;
          while (
            prefetchCursor < N &&
            (deadline.timeRemaining() > 1 || deadline.didTimeout)
          ) {
            warmItem(prefetchCursor++);
            while (prefetchCursor < N && isWarm(prefetchCursor))
              prefetchCursor++;
          }
          if (prefetchCursor < N) scheduleIdlePrefetch();
        },
        { timeout: IDLE_PREFETCH_TIMEOUT_MS },
      );
      cancelPrefetch = () => window.cancelIdleCallback(handle);
    } else {
      const handle = window.setTimeout(() => {
        cancelPrefetch = null;
        const end = Math.min(prefetchCursor + IDLE_PREFETCH_FALLBACK_BATCH, N);
        while (prefetchCursor < end) {
          warmItem(prefetchCursor++);
          while (prefetchCursor < end && isWarm(prefetchCursor))
            prefetchCursor++;
        }
        if (prefetchCursor < N) scheduleIdlePrefetch();
      }, IDLE_PREFETCH_FALLBACK_DELAY_MS);
      cancelPrefetch = () => window.clearTimeout(handle);
    }
  }

  function createSlot(): SlotEntry {
    const el = document.createElement("div");
    Object.assign(el.style, {
      position: "absolute",
      top: "0",
      left: "0",
      width: itemW + "px",
      height: itemH + "px",
      // `paint` excluded from contain — including it would clip the card's scale-up animation at slot boundaries.
      contain: "layout paint size",
    } as Partial<CSSStyleDeclaration>);
    inner.appendChild(el);
    return {
      el,
      cardEl: null,
      col: 0,
      row: 0,
      itemIndex: -1,
      active: false,
    };
  }

  function assignSlot(slot: SlotEntry, col: number, row: number) {
    slot.col = col;
    slot.row = row;
    slot.el.style.transform = `translate3d(${col * cellW}px, ${row * cellH}px, 0)`;
    slot.el.style.display = "";
    const idx = itemAt(col, row);
    const card = ensureItemCard(idx);
    card.style.filter =
      searchDimSet && !searchDimSet.has(idx) ? SEARCH_DIM_FILTER : "";
    slot.el.appendChild(card);
    slot.cardEl = card;
    slot.itemIndex = idx;
    slot.active = false;
  }

  // Reset a slot's card to its mount state and detach it, returning it to the item cache
  // (primary card) or the spares pool (duplicate clone) so it's always reusable.
  function detachCard(slot: SlotEntry) {
    const card = slot.cardEl;
    if (!card) return;
    gsap.killTweensOf(card);
    gsap.set(card, { scale: MOUNT_SCALE, opacity: 0 });
    card.style.willChange = "";
    const imgs = getCardImgs(card);
    if (imgs.length) {
      gsap.killTweensOf(imgs);
      gsap.set(imgs, { scale: IMG_FROM_SCALE });
    }
    if (card.parentNode === slot.el) {
      slot.el.removeChild(card);
    }
    if (slot.itemIndex >= 0 && card !== itemCards[slot.itemIndex]) {
      const spares = spareCards.get(slot.itemIndex);
      if (spares) spares.push(card);
      else spareCards.set(slot.itemIndex, [card]);
    }
    slot.cardEl = null;
  }

  function recycleSlot(slot: SlotEntry) {
    detachCard(slot);
    slot.active = false;
    slot.itemIndex = -1;
    slot.el.style.display = "none";
    pool.push(slot);
  }

  let lastColMin = NaN,
    lastColMax = NaN,
    lastRowMin = NaN,
    lastRowMax = NaN;

  // Shifts state.x/y by whole-tile multiples and adjusts slot coords to match — visually a no-op,
  // but prevents float-precision drift after long panning. Skipped while GSAP tweens state.x/y.
  function maybeResetOrigin() {
    const tileX = vCols * cellW * state.z;
    const tileY = vRows * cellH * state.z;
    if (Math.abs(state.x) < tileX && Math.abs(state.y) < tileY) return;
    if (gsap.isTweening(state)) return;
    const shiftX = Math.round(state.x / tileX);
    const shiftY = Math.round(state.y / tileY);
    if (shiftX === 0 && shiftY === 0) return;
    state.x -= shiftX * tileX;
    state.y -= shiftY * tileY;
    const dCol = shiftX * vCols;
    const dRow = shiftY * vRows;
    const slots = Array.from(activeByKey.values());
    activeByKey.clear();
    for (const slot of slots) {
      slot.col += dCol;
      slot.row += dRow;
      slot.el.style.transform = `translate3d(${slot.col * cellW}px, ${slot.row * cellH}px, 0)`;
      activeByKey.set(cellKey(slot.col, slot.row), slot);
    }
    lastColMin += dCol;
    lastColMax += dCol;
    lastRowMin += dRow;
    lastRowMax += dRow;
  }

  function updateSlots() {
    const vw = element.clientWidth;
    const vh = element.clientHeight;
    const zCellW = cellW * state.z;
    const zCellH = cellH * state.z;
    // Buffer rings get physically longer when zoomed out — scale them down so the off-screen
    // card count doesn't balloon at low zoom.
    const buffer = Math.max(1, Math.round(BUFFER_CELLS * state.z));
    const colMin = Math.floor(-state.x / zCellW) - buffer;
    const colMax = Math.ceil((vw - state.x) / zCellW) + buffer;
    const rowMin = Math.floor(-state.y / zCellH) - buffer;
    const rowMax = Math.ceil((vh - state.y) / zCellH) + buffer;

    if (
      colMin === lastColMin &&
      colMax === lastColMax &&
      rowMin === lastRowMin &&
      rowMax === lastRowMax
    ) {
      return;
    }
    lastColMin = colMin;
    lastColMax = colMax;
    lastRowMin = rowMin;
    lastRowMax = rowMax;

    for (const [key, slot] of activeByKey) {
      if (
        slot.col < colMin ||
        slot.col > colMax ||
        slot.row < rowMin ||
        slot.row > rowMax
      ) {
        recycleSlot(slot);
        activeByKey.delete(key);
      }
    }
    for (let r = rowMin; r <= rowMax; r++) {
      for (let c = colMin; c <= colMax; c++) {
        const key = cellKey(c, r);
        if (activeByKey.has(key)) continue;
        const slot = pool.pop() || createSlot();
        assignSlot(slot, c, r);
        activeByKey.set(key, slot);
      }
    }
  }

  function updateScaleTriggers() {
    if (!inView) return;
    const vw = element.clientWidth;
    const vh = element.clientHeight;
    if (vw === 0 || vh === 0) return;
    // All in screen px: content distances scale by the current zoom.
    const z = state.z;
    const cardHalfW = (itemW / 2) * z;
    const cardHalfH = (itemH / 2) * z;
    const halfVw = vw / 2;
    const halfVh = vh / 2;
    const enterX = cardHalfW - ENTER_MARGIN * itemW * z;
    const enterY = cardHalfH - ENTER_MARGIN * itemH * z;
    const exitX = cardHalfW + EXIT_MARGIN * itemW * z;
    const exitY = cardHalfH + EXIT_MARGIN * itemH * z;

    const entering: { card: HTMLElement; dist: number }[] = [];

    for (const slot of activeByKey.values()) {
      if (!slot.cardEl) continue;
      const cx = state.x + slot.col * cellW * z + cardHalfW;
      const cy = state.y + slot.row * cellH * z + cardHalfH;

      const inside =
        cx > -enterX && cx < vw + enterX && cy > -enterY && cy < vh + enterY;
      const stillIn =
        cx > -exitX && cx < vw + exitX && cy > -exitY && cy < vh + exitY;

      if (!slot.active && inside) {
        slot.active = true;
        const dx = cx - halfVw;
        const dy = cy - halfVh;
        entering.push({
          card: slot.cardEl,
          dist: Math.sqrt(dx * dx + dy * dy),
        });
      } else if (slot.active && !stillIn) {
        slot.active = false;
        playCard(slot.cardEl, EDGE_SCALE, 0, 0);
      }
    }

    if (entering.length > 1) entering.sort((a, b) => a.dist - b.dist);
    for (let i = 0; i < entering.length; i++) {
      playCard(
        entering[i]!.card,
        CENTER_SCALE,
        1,
        Math.min(i * PLAY_STAGGER, PLAY_DELAY_MAX),
        instantReveal || i >= PLAY_MAX_ANIMATED,
      );
    }
  }

  function playCard(
    card: HTMLElement,
    scale: number,
    opacity: number,
    delay: number,
    instant = false,
  ) {
    const imgScale = opacity >= 1 ? IMG_TO_SCALE : IMG_FROM_SCALE;
    const imgs = getCardImgs(card);
    gsap.killTweensOf(card);
    if (imgs.length) gsap.killTweensOf(imgs);
    if (reduced || instant) {
      gsap.set(card, { scale, opacity });
      if (imgs.length) gsap.set(imgs, { scale: imgScale });
      return;
    }
    // Compositor layer only while animating; recycleSlot clears it if the tween dies early.
    card.style.willChange = "transform, opacity";
    gsap.to(card, {
      scale,
      opacity,
      duration: PLAY_DURATION,
      ease: PLAY_EASE,
      delay,
      onComplete: () => {
        card.style.willChange = "";
      },
    });
    if (imgs.length) {
      gsap.to(imgs, {
        scale: imgScale,
        duration: PLAY_DURATION,
        ease: PLAY_EASE,
        delay,
      });
    }
  }

  // Narrow viewports open zoomed-out so several faces are visible; wider ones fit more than
  // OPEN_COLS at full zoom and clamp back to ZOOM_MAX, keeping the desktop resting state. cellW is
  // already measured above; guard a zero-width mount by falling back to full zoom.
  const openZoom = (() => {
    const vw = element.clientWidth;
    if (vw === 0 || cellW === 0) return ZOOM_MAX;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, vw / (OPEN_COLS * cellW)));
  })();
  const state = { x: 0, y: 0, z: openZoom };
  // Where the zoom is headed — may be ahead of state.z mid-tween, so rapid clicks step from intent.
  let zoomTarget = openZoom;
  // Item indices matching the active search; everyone else gets dimmed. Null = no active search.
  let searchDimSet: Set<number> | null = null;
  let isDown = false;
  let isDragging = false;
  let startX = 0,
    startY = 0;
  let lastX = 0,
    lastY = 0;
  let lastT = 0;
  let velX = 0,
    velY = 0;
  let pointerId: number | null = null;
  // Gate scale plays until the section is first scrolled into view so the staggered reveal is actually seen.
  let inView = false;
  // When set, the next reveal snaps every visible card to full scale instead of staggering it in —
  // used for the extendRoster re-tile so folding in the full roster doesn't blink the whole grid
  // out and stagger it back (which reads as gaps).
  let instantReveal = false;

  // Roster-extend bookkeeping: the background-loaded tail folds into the torus exactly once,
  // deferred past any live interaction (see scheduleExtend / extendRoster).
  let isRebuilding = false;
  let extendRaf: number | null = null;
  let extendDeadline = 0;

  const activePointers = new Map<number, { x: number; y: number }>();
  let pinching = false;
  let pinchStartDist = 0;
  let pinchStartZoom = ZOOM_MAX;
  // Content-space point under the pinch midpoint — held fixed under the fingers while scaling.
  let pinchAnchorX = 0;
  let pinchAnchorY = 0;
  let pinchRect: DOMRect | null = null;

  function render() {
    maybeResetOrigin();
    inner.style.transform = `translate3d(${state.x}px, ${state.y}px, 0) scale(${state.z})`;
    updateSlots();
    updateScaleTriggers();
  }

  function centerOnInit() {
    const vw = element.clientWidth;
    const vh = element.clientHeight;
    state.x = vw / 2 - (itemW / 2) * state.z;
    state.y = vh / 2 - (itemH / 2) * state.z;
  }

  function prewarmPool() {
    const vw = element.clientWidth;
    const vh = element.clientHeight;
    if (vw === 0 || vh === 0 || cellW === 0 || cellH === 0) return;
    const buffer = Math.max(1, Math.round(BUFFER_CELLS * state.z));
    const cols = Math.ceil(vw / (cellW * state.z)) + 2 * buffer + 1;
    const rows = Math.ceil(vh / (cellH * state.z)) + 2 * buffer + 1;
    const target = cols * rows;
    while (pool.length < target) {
      const slot = createSlot();
      slot.el.style.display = "none";
      pool.push(slot);
    }
  }

  centerOnInit();
  prewarmPool();
  render();
  startPrefetchWhenReady();

  onView(element, {
    autoStart: true,
    once: true,
    callback: ({ isIn }) => {
      if (isIn && !inView) {
        inView = true;
        render();
      }
    },
  });

  // Background-load the remaining pages, then fold them in once. Not awaited — create.ts never
  // awaits a module's default export, so this never blocks first paint.
  if (hasNextPage) {
    void loadPaginatedItems(cmsWrapper, cmsList, loadAbort.signal)
      .then(() => {
        if (!destroyed) scheduleExtend();
      })
      .catch(() => {});
  }

  function removePointerListeners() {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
  }

  function resetPointers() {
    for (const id of activePointers.keys()) {
      if (viewport.hasPointerCapture(id)) viewport.releasePointerCapture(id);
    }
    activePointers.clear();
    pinching = false;
    pinchRect = null;
    pointerId = null;
    isDown = false;
    removePointerListeners();
    viewport.style.cursor = "grab";
  }

  // (Re-)baseline the pinch from the two current pointers; also called when a third finger
  // lifts so the remaining pair keeps scaling smoothly.
  function startPinch() {
    const pts = [...activePointers.values()];
    if (pts.length < 2) return;
    pinching = true;
    isDragging = false;
    gsap.killTweensOf(state);
    pinchRect = element.getBoundingClientRect();
    pinchStartDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
    pinchStartZoom = state.z;
    const midX = (pts[0].x + pts[1].x) / 2 - pinchRect.left;
    const midY = (pts[0].y + pts[1].y) / 2 - pinchRect.top;
    pinchAnchorX = (midX - state.x) / state.z;
    pinchAnchorY = (midY - state.y) / state.z;
  }

  function onPointerDown(e: PointerEvent) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (activePointers.size === 0) {
      // Window-level listeners ensure pointerup is caught if pointer leaves the viewport or capture is lost mid-drag.
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
      window.addEventListener("pointercancel", onPointerUp);
    }
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try {
      viewport.setPointerCapture(e.pointerId);
    } catch {
      // setPointerCapture throws if the pointer is no longer active; window listeners cover us either way.
    }
    if (activePointers.size === 2) {
      startPinch();
      return;
    }
    if (activePointers.size > 2) return; // extra fingers ride along, tracked but inert
    isDown = true;
    isDragging = false;
    pointerId = e.pointerId;
    startX = lastX = e.clientX;
    startY = lastY = e.clientY;
    lastT = performance.now();
    velX = 0;
    velY = 0;
    gsap.killTweensOf(state);
    // A killed zoom tween leaves state.z mid-flight; future zoom clicks step from reality.
    zoomTarget = state.z;
  }

  function onPointerUp(e: PointerEvent) {
    if (!activePointers.has(e.pointerId)) return;
    activePointers.delete(e.pointerId);
    if (viewport.hasPointerCapture(e.pointerId)) {
      viewport.releasePointerCapture(e.pointerId);
    }
    if (pinching) {
      if (activePointers.size >= 2) {
        startPinch();
        return;
      }
      pinching = false;
      pinchRect = null;
      updateZoomButtons();
      if (activePointers.size === 1) {
        // Hand the remaining finger off to a regular pan without requiring a re-touch.
        const [[id, pt]] = activePointers.entries();
        pointerId = id;
        isDown = true;
        isDragging = true;
        startX = lastX = pt.x;
        startY = lastY = pt.y;
        lastT = performance.now();
        velX = 0;
        velY = 0;
        return;
      }
      resetPointers();
      return;
    }
    if (activePointers.size > 0) return; // a non-primary pointer lifted
    if (e.pointerId !== pointerId) {
      resetPointers();
      return;
    }
    endDrag();
  }

  function onLostCapture() {
    // Browser took the pointer (gesture intercept, element removal, etc.) — treat as release.
    endDrag();
  }

  function onVisibilityChange() {
    if (document.hidden && (isDown || pinching)) endDrag();
  }

  function onPointerMove(e: PointerEvent) {
    const pt = activePointers.get(e.pointerId);
    if (pt) {
      pt.x = e.clientX;
      pt.y = e.clientY;
    }
    if (pinching) {
      if (!pt || !pinchRect) return;
      const pts = [...activePointers.values()];
      if (pts.length < 2) return;
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const z = Math.min(
        ZOOM_MAX,
        Math.max(ZOOM_MIN, pinchStartZoom * (dist / pinchStartDist)),
      );
      const midX = (pts[0].x + pts[1].x) / 2 - pinchRect.left;
      const midY = (pts[0].y + pts[1].y) / 2 - pinchRect.top;
      state.z = z;
      state.x = midX - pinchAnchorX * z;
      state.y = midY - pinchAnchorY * z;
      zoomTarget = z;
      render();
      return;
    }
    if (!isDown || e.pointerId !== pointerId) return;
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;

    if (!isDragging) {
      const totalDx = e.clientX - startX;
      const totalDy = e.clientY - startY;
      if (Math.hypot(totalDx, totalDy) < DRAG_THRESHOLD) {
        lastX = e.clientX;
        lastY = e.clientY;
        lastT = performance.now();
        return;
      }
      isDragging = true;
      viewport.style.cursor = "grabbing";
    }

    const now = performance.now();
    const dt = Math.max(1, now - lastT);

    state.x += dx;
    state.y += dy;

    const alpha = Math.min(1, dt / VELOCITY_EMA_MS);
    velX = velX * (1 - alpha) + (dx / dt) * 1000 * alpha;
    velY = velY * (1 - alpha) + (dy / dt) * 1000 * alpha;

    lastX = e.clientX;
    lastY = e.clientY;
    lastT = now;
    render();
  }

  function endDrag() {
    if (!isDown && !pinching && activePointers.size === 0) return;
    const flick = isDragging && !pinching;
    isDragging = false;
    const wasPinching = pinching;
    resetPointers();
    if (wasPinching) updateZoomButtons();

    if (!flick || destroyed) return;

    gsap.to(state, {
      inertia: {
        x: { velocity: velX },
        y: { velocity: velY },
        resistance: INERTIA_RESISTANCE,
      },
      onUpdate: render,
      // Origin reset is gated on !gsap.isTweening(state); run render() on complete to give it a chance now.
      onComplete: render,
    });
  }

  function onClickCapture(e: MouseEvent) {
    const totalDx = e.clientX - startX;
    const totalDy = e.clientY - startY;
    if (Math.hypot(totalDx, totalDy) >= DRAG_THRESHOLD) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  function onWheel(e: WheelEvent) {
    e.preventDefault();
    if (gsap.isTweening(state)) {
      gsap.killTweensOf(state);
      zoomTarget = state.z;
    }
    let dx = e.deltaX;
    let dy = e.deltaY;
    if (e.deltaMode === 1) {
      dx *= WHEEL_LINE_HEIGHT_PX;
      dy *= WHEEL_LINE_HEIGHT_PX;
    } else if (e.deltaMode === 2) {
      dx *= WHEEL_PAGE_HEIGHT_PX;
      dy *= WHEEL_PAGE_HEIGHT_PX;
    }
    state.x -= dx * WHEEL_MULTIPLIER;
    state.y -= dy * WHEEL_MULTIPLIER;
    render();
  }

  viewport.addEventListener("pointerdown", onPointerDown);
  viewport.addEventListener("lostpointercapture", onLostCapture);
  viewport.addEventListener("click", onClickCapture, { capture: true });
  document.addEventListener("visibilitychange", onVisibilityChange);
  if (wheelEnabled) {
    viewport.addEventListener("wheel", onWheel, { passive: false });
  }

  const searchInput = element.querySelector<HTMLInputElement>(
    '[team-grid-infinite="search"]',
  );
  const searchNames: string[] = searchInput
    ? items.map((item) => {
        let t = "";
        item
          .querySelectorAll<HTMLElement>('[team-grid-infinite="name"]')
          .forEach((el) => (t += " " + (el.textContent || "")));
        return foldText(t);
      })
    : [];
  const searchCount = element.querySelector<HTMLElement>(
    '[team-grid-infinite="search-count"]',
  );
  // The canvas is aria-hidden, so the count chip is the only feedback channel — announce match
  // changes (and the "still loading" state during the background roster load) to screen readers.
  if (searchCount) searchCount.setAttribute("aria-live", "polite");
  const searchCountWrap = element.querySelector<HTMLElement>(
    '[team-grid-infinite="search-count-wrap"]',
  );
  // Wrap starts hidden; revealed only while a search is active so the empty count chip
  // doesn't sit on the canvas at rest. Falls back to the count element if no wrap exists.
  const prevCountWrapDisplay = searchCountWrap?.style.display ?? "";
  if (searchCountWrap) searchCountWrap.style.display = "none";
  let placements: number[][] | null = null;
  let searchActive = false;
  let searchMatches: number[] = [];
  let searchMatchPos = 0;
  let searchTimer: number | null = null;

  // Each item exists at several spots on the torus, each repeating infinitely in both axes;
  // returns the copy closest to the current viewport center (cell coords + px² distance).
  function nearestPlacement(idx: number, vw: number, vh: number) {
    if (!placements) {
      placements = Array.from({ length: N }, () => []);
      for (let k = 0; k < layout.length; k++) {
        placements[layout[k]].push(k % vCols, (k / vCols) | 0);
      }
    }
    const cells = placements[idx];
    if (!cells || cells.length === 0) return null;
    const colF = ((vw / 2 - state.x) / state.z - itemW / 2) / cellW;
    const rowF = ((vh / 2 - state.y) / state.z - itemH / 2) / cellH;
    let c = 0,
      r = 0,
      d = Infinity;
    for (let i = 0; i < cells.length; i += 2) {
      const pc = cells[i] + Math.round((colF - cells[i]) / vCols) * vCols;
      const pr =
        cells[i + 1] + Math.round((rowF - cells[i + 1]) / vRows) * vRows;
      const dx = (pc - colF) * cellW * state.z;
      const dy = (pr - rowF) * cellH * state.z;
      const pd = dx * dx + dy * dy;
      if (pd < d) {
        d = pd;
        c = pc;
        r = pr;
      }
    }
    return { c, r, d };
  }

  function goToItem(idx: number) {
    const vw = element.clientWidth;
    const vh = element.clientHeight;
    if (vw === 0 || vh === 0) return;
    const p = nearestPlacement(idx, vw, vh);
    if (!p) return;

    gsap.killTweensOf(state);
    const targetX = vw / 2 - (p.c * cellW + itemW / 2) * state.z;
    const targetY = vh / 2 - (p.r * cellH + itemH / 2) * state.z;
    if (reduced) {
      state.x = targetX;
      state.y = targetY;
      render();
      return;
    }
    const duration = Math.min(
      SEARCH_FLY_MAX_DURATION,
      Math.max(SEARCH_FLY_MIN_DURATION, Math.sqrt(p.d) / SEARCH_FLY_SPEED_PX_S),
    );
    gsap.to(state, {
      x: targetX,
      y: targetY,
      duration,
      ease: SEARCH_FLY_EASE,
      onUpdate: render,
      // Origin reset is gated on !gsap.isTweening(state); run render() on complete to give it a chance now.
      onComplete: render,
    });
  }

  function applySearchHighlight() {
    for (const slot of activeByKey.values()) {
      if (!slot.cardEl) continue;
      slot.cardEl.style.filter =
        searchDimSet && !searchDimSet.has(slot.itemIndex)
          ? SEARCH_DIM_FILTER
          : "";
    }
  }

  function updateSearchCount() {
    if (searchCountWrap) {
      searchCountWrap.style.display = searchActive ? "" : "none";
    }
    if (!searchCount) return;
    if (!searchActive) {
      searchCount.textContent = "";
    } else if (searchMatches.length === 0) {
      // A not-yet-loaded person reads as "indexing" rather than a false zero.
      searchCount.textContent = rosterComplete
        ? "0 matches"
        : "0 matches (still loading…)";
    } else {
      searchCount.textContent = `${searchMatchPos + 1} / ${searchMatches.length}`;
    }
  }

  function runSearch(raw: string) {
    const q = foldText(raw);
    searchMatches = [];
    searchMatchPos = 0;
    searchActive = q.length > 0;
    if (!searchActive) {
      searchDimSet = null;
      applySearchHighlight();
      updateSearchCount();
      return;
    }
    // Tiered ranking: exact word ("alex" → Alex) > word-prefix (→ Alexa) > substring ("ca" → Francesca).
    const exact: number[] = [];
    const prefix: number[] = [];
    const sub: number[] = [];
    const qWord = " " + q + " ";
    const qPrefix = " " + q;
    for (let i = 0; i < searchNames.length; i++) {
      const name = searchNames[i];
      if (!name || !name.includes(q)) continue;
      const padded = " " + name + " ";
      if (padded.includes(qWord)) exact.push(i);
      else if (padded.includes(qPrefix)) prefix.push(i);
      else sub.push(i);
    }
    // Within each tier, nearest first so Enter-cycling hops outward instead of teleporting.
    const vw = element.clientWidth;
    const vh = element.clientHeight;
    const byDistance = (arr: number[]) =>
      arr
        .map((i) => [i, nearestPlacement(i, vw, vh)?.d ?? Infinity] as const)
        .sort((a, b) => a[1] - b[1])
        .map(([i]) => i);
    searchMatches = byDistance(exact).concat(
      byDistance(prefix),
      byDistance(sub),
    );
    searchDimSet = searchMatches.length > 0 ? new Set(searchMatches) : null;
    applySearchHighlight();
    updateSearchCount();
    if (searchMatches.length > 0) goToItem(searchMatches[0]);
  }

  function onSearchInput() {
    if (searchTimer !== null) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      searchTimer = null;
      runSearch(searchInput!.value);
    }, SEARCH_DEBOUNCE_MS);
  }

  function onSearchKeydown(e: KeyboardEvent) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (searchTimer !== null) {
      window.clearTimeout(searchTimer);
      searchTimer = null;
      runSearch(searchInput!.value);
      return;
    }
    if (searchMatches.length === 0) return;
    searchMatchPos = (searchMatchPos + 1) % searchMatches.length;
    updateSearchCount();
    goToItem(searchMatches[searchMatchPos]);
  }

  if (searchInput) {
    // The section disables user-select/touch-action for dragging; restore them on the field.
    searchInput.style.userSelect = "text";
    searchInput.style.touchAction = "auto";
    searchInput.addEventListener("input", onSearchInput);
    searchInput.addEventListener("keydown", onSearchKeydown);
  }

  const zoomInBtn = element.querySelector<HTMLButtonElement>(
    '[team-grid-infinite="zoom-in"]',
  );
  const zoomOutBtn = element.querySelector<HTMLButtonElement>(
    '[team-grid-infinite="zoom-out"]',
  );

  function updateZoomButtons() {
    if (zoomInBtn) {
      const off = zoomTarget >= ZOOM_MAX - 1e-3;
      zoomInBtn.disabled = off;
      zoomInBtn.style.opacity = off ? "0.35" : "";
    }
    if (zoomOutBtn) {
      const off = zoomTarget <= ZOOM_MIN + 1e-3;
      zoomOutBtn.disabled = off;
      zoomOutBtn.style.opacity = off ? "0.35" : "";
    }
  }

  function zoomBy(factor: number) {
    zoomTarget = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoomTarget * factor));
    updateZoomButtons();
    if (zoomTarget === state.z) return;
    const vw = element.clientWidth;
    const vh = element.clientHeight;
    if (vw === 0 || vh === 0) {
      state.z = zoomTarget;
      render();
      return;
    }
    // Anchor the content point under the viewport center. target x/y are linear in z,
    // so tweening x/y/z together with one ease keeps the anchor fixed for the whole tween.
    const cx = (vw / 2 - state.x) / state.z;
    const cy = (vh / 2 - state.y) / state.z;
    const targetX = vw / 2 - cx * zoomTarget;
    const targetY = vh / 2 - cy * zoomTarget;
    gsap.killTweensOf(state);
    if (reduced) {
      state.x = targetX;
      state.y = targetY;
      state.z = zoomTarget;
      render();
      return;
    }
    gsap.to(state, {
      x: targetX,
      y: targetY,
      z: zoomTarget,
      duration: ZOOM_DURATION,
      ease: ZOOM_EASE,
      onUpdate: render,
      onComplete: render,
    });
  }

  const onZoomIn = () => zoomBy(1 / ZOOM_STEP);
  const onZoomOut = () => zoomBy(ZOOM_STEP);
  if (zoomInBtn) zoomInBtn.addEventListener("click", onZoomIn);
  if (zoomOutBtn) zoomOutBtn.addEventListener("click", onZoomOut);
  updateZoomButtons();

  let lastVw = element.clientWidth;
  let lastVh = element.clientHeight;

  function clearAllSlots() {
    for (const slot of activeByKey.values()) {
      // Detach cards BEFORE removing the slot — a card left inside a removed slot keeps a
      // parentNode, which made ensureItemCard treat the whole cache as in-use after a resize
      // rebuild: every card became a fresh throwaway clone, degrading long sessions.
      detachCard(slot);
      slot.el.remove();
    }
    activeByKey.clear();
    for (const slot of pool) slot.el.remove();
    pool.length = 0;
    lastColMin = lastColMax = lastRowMin = lastRowMax = NaN;
  }

  function remeasure() {
    if (isRebuilding) return; // extend owns the slots/pool during its synchronous swap
    const r = items[0].getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    const vw = element.clientWidth;
    const vh = element.clientHeight;
    const itemChanged =
      Math.abs(r.width - itemW) > RESIZE_REBUILD_THRESHOLD_PX ||
      Math.abs(r.height - itemH) > RESIZE_REBUILD_THRESHOLD_PX;
    const viewportChanged = vw !== lastVw || vh !== lastVh;
    if (!itemChanged && !viewportChanged) return;
    if (itemChanged) {
      itemW = r.width;
      itemH = r.height;
      cellW = itemW + GAP;
      cellH = itemH + GAP;
    }
    lastVw = vw;
    lastVh = vh;
    if (itemChanged) {
      clearAllSlots();
      prewarmPool();
      const sizesHint = Math.round(itemW) + "px";
      for (const card of itemCards) {
        if (!card) continue;
        for (const img of getCardImgs(card)) img.sizes = sizesHint;
      }
    }
    render();
  }
  const offResize = Resize.add(remeasure);

  // Defer the one-time roster extend until nothing live would be corrupted or visibly yanked: no
  // state tween (drag-inertia/zoom/search-fly), no in-flight card reveals, and the search field
  // idle. A hard cap forces it through so a continuous dragger still eventually gets everyone —
  // extendRoster kills any live tween on entry, so the forced path is safe by construction.
  function scheduleExtend() {
    if (destroyed || isRebuilding || rosterComplete) return;
    if (extendDeadline === 0) extendDeadline = performance.now() + EXTEND_DEFER_MAX_MS;
    const forced = performance.now() >= extendDeadline;
    const idle =
      !gsap.isTweening(state) &&
      activeTweenCount() === 0 &&
      !searchActive &&
      searchTimer === null &&
      document.activeElement !== searchInput;
    if (!idle && !forced) {
      extendRaf = requestAnimationFrame(() => {
        extendRaf = null;
        scheduleExtend();
      });
      return;
    }
    extendRaf = null;
    extendRoster();
  }

  // Fold the background-loaded tail into the live torus exactly once. Webflow paginates in
  // document order, so indices 0..oldN-1 are byte-stable — every already-built clone and warm bit
  // stays valid; only the bigger torus sequence is regenerated (the one visible re-tile).
  function extendRoster() {
    if (destroyed || isRebuilding) return;
    const fresh = Array.from(
      cmsList!.querySelectorAll<HTMLElement>('[team-grid-infinite="cms-item"]'),
    );
    if (fresh.length <= N) {
      rosterComplete = true;
      return; // nothing new — also makes a redundant call a no-op
    }
    isRebuilding = true;
    try {
      // Safe entry regardless of how we got here (idle gate OR forced cap): kill the state tween
      // and every live per-card reveal before any layout read, so no frame reads a half-swapped
      // torus. zoomTarget resyncs to reality, mirroring the other manual killTweensOf(state).
      gsap.killTweensOf(state);
      zoomTarget = state.z;
      if (searchTimer !== null) {
        window.clearTimeout(searchTimer);
        searchTimer = null;
      }
      // Stop in-flight idle prefetch before reallocating `prefetched`, so no queued callback
      // indexes the about-to-be-detached typed array.
      if (cancelPrefetch) {
        cancelPrefetch();
        cancelPrefetch = null;
      }
      for (const card of itemCards) {
        if (!card) continue;
        gsap.killTweensOf(card);
        const imgs = getCardImgs(card);
        if (imgs.length) gsap.killTweensOf(imgs);
      }

      const oldN = N;
      items = fresh;
      N = items.length;
      ({ vCols, vRows } = computeTorus(N));

      // Grow the warm-bit array, preserving page-1 warmth (no re-decode of already-seen images).
      const oldPrefetched = prefetched;
      prefetched = new Uint8Array(N);
      prefetched.set(oldPrefetched.subarray(0, Math.min(oldN, N)));

      // Keep existing clones; the new tail starts uniformly null (never undefined).
      itemCards.length = N;
      itemCards.fill(null, oldN);

      // Extend the search index in index-lockstep (only the new tail).
      if (searchInput) {
        for (let i = oldN; i < N; i++) {
          let t = "";
          items[i]
            .querySelectorAll<HTMLElement>('[team-grid-infinite="name"]')
            .forEach((el) => (t += " " + (el.textContent || "")));
          searchNames.push(foldText(t));
        }
      }

      placements = null; // memoized against the old layout/N — force a lazy rebuild
      layout = buildLayout(); // the one unavoidable reshuffle for the bigger torus

      // Atomic visible re-tile (mirrors remeasure's itemChanged path). Order matters:
      // clearAllSlots empties activeByKey + resets lastCol/Row to NaN; spareCards is dropped
      // (its duplicate clones are stale and grow unbounded over a session); then centerOnInit
      // recenters so the first post-swap maybeResetOrigin sees a consistent tile period.
      clearAllSlots();
      spareCards.clear();
      prewarmPool();
      centerOnInit();
      // Snap the re-tiled cards in rather than replaying the staggered reveal — the swap should be
      // invisible, not a full grid blink.
      instantReveal = true;
      render();
      instantReveal = false;

      if (prefetchEnabled) scheduleIdlePrefetch(); // warm into the new tail
      if (searchActive && searchInput) runSearch(searchInput.value); // re-find vs the full roster
      rosterComplete = true;
    } finally {
      isRebuilding = false;
    }
  }

  const dbg = {
    longFrames: 0,
    fps: [] as number[],
    lastFrame: performance.now(),
    windowStart: performance.now(),
  };
  let dbgSummaryTimer: number | null = null;
  let dbgOffRaf: (() => void) | null = null;
  function activeTweenCount() {
    let n = 0;
    for (const slot of activeByKey.values()) {
      if (slot.cardEl && gsap.isTweening(slot.cardEl)) n++;
    }
    return n;
  }
  function cachedCount() {
    let n = 0;
    for (const c of itemCards) if (c) n++;
    return n;
  }
  if (DEBUG) {
    dbgOffRaf = Raf.add(() => {
      const now = performance.now();
      const frameMs = now - dbg.lastFrame;
      dbg.lastFrame = now;
      if (frameMs > 0 && frameMs < 1000) {
        dbg.fps.push(1000 / frameMs);
        if (frameMs > DEBUG_LONG_FRAME_MS) dbg.longFrames++;
      }
    });
    dbgSummaryTimer = window.setInterval(() => {
      const dt = (performance.now() - dbg.windowStart) / 1000;
      const fps = dbg.fps.length
        ? dbg.fps.reduce((a, b) => a + b, 0) / dbg.fps.length
        : 0;
      const minFps = dbg.fps.length ? Math.min(...dbg.fps) : 0;
      // eslint-disable-next-line no-console
      console.log(
        `[team-grid] active=${activeByKey.size} pool=${pool.length} ` +
          `cached=${cachedCount()}/${N} tweens=${activeTweenCount()} ` +
          `fps=${fps.toFixed(1)} (min ${minFps.toFixed(1)}) ` +
          `longFrames=${dbg.longFrames} ` +
          `state=(${state.x.toFixed(0)},${state.y.toFixed(0)}) ` +
          `win=${dt.toFixed(1)}s`,
      );
      dbg.longFrames = 0;
      dbg.fps.length = 0;
      dbg.windowStart = performance.now();
    }, DEBUG_INTERVAL_MS);
  }

  onDestroy(() => {
    destroyed = true;
    endDrag();
    if (searchTimer !== null) {
      window.clearTimeout(searchTimer);
      searchTimer = null;
    }
    if (searchInput) {
      searchInput.removeEventListener("input", onSearchInput);
      searchInput.removeEventListener("keydown", onSearchKeydown);
    }
    if (zoomInBtn) zoomInBtn.removeEventListener("click", onZoomIn);
    if (zoomOutBtn) zoomOutBtn.removeEventListener("click", onZoomOut);
    offResize();
    if (extendRaf !== null) {
      cancelAnimationFrame(extendRaf);
      extendRaf = null;
    }
    if (cancelPrefetch) {
      cancelPrefetch();
      cancelPrefetch = null;
    }
    if (dbgOffRaf) dbgOffRaf();
    if (dbgSummaryTimer !== null) window.clearInterval(dbgSummaryTimer);
    gsap.killTweensOf(state);
    for (const card of itemCards) {
      if (!card) continue;
      gsap.killTweensOf(card);
      const imgs = getCardImgs(card);
      if (imgs.length) gsap.killTweensOf(imgs);
    }
    clearAllSlots();
    viewport.removeEventListener("pointerdown", onPointerDown);
    viewport.removeEventListener("lostpointercapture", onLostCapture);
    viewport.removeEventListener("click", onClickCapture, { capture: true });
    document.removeEventListener("visibilitychange", onVisibilityChange);
    if (wheelEnabled) viewport.removeEventListener("wheel", onWheel);
    viewport.remove();
    if (searchCountWrap) searchCountWrap.style.display = prevCountWrapDisplay;
    paginationWraps.forEach((el, i) => {
      el.style.display = prevPaginationDisplay[i] ?? "";
    });
    cmsWrapper.style.opacity = prevWrapperOpacity;
    cmsWrapper.style.pointerEvents = prevWrapperPointer;
    element.style.position = prevSectionPosition;
    element.style.touchAction = prevSectionTouchAction;
    element.style.userSelect = prevSectionUserSelect;
  });
}
